import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, ShoppingCart, ScanBarcode, CheckCircle, Package, Printer, XCircle, Building2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { DirectShipEwbPending } from '@/components/ewayBill/DirectShipEwbPending';

const AssortmentShip = () => {
  const navigate = useNavigate();
  const bundleInputRef = useRef(null);

  // Toggle: 'site' | 'customer'
  const [shipType, setShipType] = useState('site');

  // Site transfer
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');

  // Customer sale
  const [pickSlips, setPickSlips] = useState([]);
  const [selectedSlip, setSelectedSlip] = useState('');

  // Bundle scanning
  const [bundleValue, setBundleValue] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [scanning, setScanning] = useState(false);

  // Result
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  // Legacy Quick Ship mode
  const [legacyMode, setLegacyMode] = useState(false);

  useEffect(() => {
    apiClient.get('/assortment-ops/ship/sites').then(r => setSites(r.data.sites || [])).catch(() => {});
    apiClient.get('/assortment-ops/ship/pick-slips').then(r => setPickSlips(r.data.pick_slips || [])).catch(() => {});
  }, []);

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const code = bundleValue.trim();
    if (!code) return;
    if (scannedBundles.some(b => b.bundle_code === code)) { toast.error('Already scanned'); setBundleValue(''); return; }

    setScanning(true);
    try {
      if (legacyMode) {
        // Validate against legacy collections so the user gets immediate feedback + bundle context
        const r = await apiClient.get('/assortment-ops/ship/validate-legacy-bundle', { params: { bundle_code: code } });
        const d = r.data;
        setScannedBundles(prev => [...prev, {
          bundle_code: d.bundle_code,
          usn_count: d.usn_count,
          design_name: d.design_name,
          shade: d.shade,
          size: d.size,
          is_legacy: !!d.is_legacy,
          source: d.source,
        }]);
        toast.success(`Added: ${d.bundle_code} · ${d.usn_count} USN(s)${d.is_legacy ? ' (legacy)' : ''}`);
      } else {
        // Fast path: no validation; backend rejects if missing
        setScannedBundles(prev => [...prev, { bundle_code: code }]);
      }
      setBundleValue('');
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to validate bundle');
    } finally { setScanning(false); }
  };

  const removeBundleAt = (idx) => setScannedBundles(prev => prev.filter((_, i) => i !== idx));

  const destination = shipType === 'site' ? selectedSite : (pickSlips.find(s => s.pick_slip_number === selectedSlip)?.customer || selectedSlip);

  const handleCreateBox = async () => {
    if (scannedBundles.length === 0) { toast.error('Scan at least one bundle'); return; }
    if (shipType === 'site' && !selectedSite) { toast.error('Select a destination site'); return; }
    if (shipType === 'customer' && !selectedSlip) { toast.error('Select a pick slip'); return; }

    setCreating(true);
    try {
      const resp = await apiClient.post('/assortment-ops/ship/create-box', {
        bundle_codes: scannedBundles.map(b => b.bundle_code),
        destination_type: shipType,
        destination_name: shipType === 'site' ? selectedSite : destination,
        pick_slip_number: shipType === 'customer' ? selectedSlip : '',
        legacy_mode: legacyMode,
      });
      toast.success(resp.data.message);
      setResult(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create box');
    } finally { setCreating(false); }
  };

  const handlePrintSlip = async () => {
    if (!result) return;
    try {
      const r = await apiClient.get(
        `/assortment-ops/ship/box/${encodeURIComponent(result.box_number)}/packing-slip`,
        { responseType: 'blob' }
      );
      const pdfBlob = r.data.type ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = `packing_slip_${result.box_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const status = err.response?.status;
      let detail = err.response?.data?.detail;
      if (!detail && err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch (_) { /* ignore */ }
      }
      toast.error(status ? `Slip PDF failed (${status}): ${detail || err.message}` : `Slip PDF failed: ${detail || err.message}`);
    }
  };

  const handleReset = () => { setScannedBundles([]); setBundleValue(''); setResult(null); };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="assortment-ship-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1"><h1 className="text-lg font-bold text-gray-900">Direct Ship</h1>
          <p className="text-xs text-gray-500">Ship packed bundles to sites or customers</p></div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/sections/inhouse/direct-ship/history')}
          data-testid="view-ship-history-btn"
        >
          <Printer className="w-4 h-4 mr-1.5" />
          History / Reprint
        </Button>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {shipType === 'site' && <DirectShipEwbPending tick={result} />}
        {!result ? (
          <>
            {/* Toggle */}
            <div className="flex rounded-lg border overflow-hidden">
              <button className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${shipType === 'site' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => setShipType('site')} data-testid="toggle-site">
                <Building2 className="w-4 h-4" /> Site Transfer
              </button>
              <button className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${shipType === 'customer' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => setShipType('customer')} data-testid="toggle-customer">
                <ShoppingCart className="w-4 h-4" /> Customer Sale
              </button>
            </div>

            {/* Destination selection */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                {shipType === 'site' ? (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Destination Site</label>
                    <Select value={selectedSite} onValueChange={setSelectedSite}>
                      <SelectTrigger data-testid="site-select"><SelectValue placeholder="Select site..." /></SelectTrigger>
                      <SelectContent>
                        {sites.map(s => <SelectItem key={s.site_name || s.site_code} value={s.site_name || s.site_code}>{s.site_name} {s.location ? `(${s.location})` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Select Pick Slip</label>
                    <Select value={selectedSlip} onValueChange={setSelectedSlip}>
                      <SelectTrigger data-testid="slip-select"><SelectValue placeholder="Select pick slip..." /></SelectTrigger>
                      <SelectContent>
                        {pickSlips.map(s => (
                          <SelectItem key={s.pick_slip_number} value={s.pick_slip_number}>
                            {s.pick_slip_number} — {s.customer} ({s.total_qty} pcs){s.boxes_created ? ` · ${s.boxes_created} box${s.boxes_created > 1 ? 'es' : ''} shipped` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Bundle scanning */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Scan Bundles</label>
                    <div className="flex items-center gap-2" data-testid="legacy-toggle-row">
                      <Label htmlFor="legacy-mode" className="text-xs text-gray-600 cursor-pointer flex items-center gap-1">
                        <AlertTriangle className={`w-3 h-3 ${legacyMode ? 'text-amber-500' : 'text-gray-400'}`} />
                        Legacy Quick Ship
                      </Label>
                      <Switch
                        id="legacy-mode"
                        checked={legacyMode}
                        onCheckedChange={setLegacyMode}
                        data-testid="legacy-mode-toggle"
                      />
                    </div>
                  </div>
                  {legacyMode && (
                    <div className="mb-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5" data-testid="legacy-mode-hint">
                      Allows shipping pre-WMS bundles directly from SFG / Goods Transfer without Assortment Packing. Inventory stock is auto-adjusted.
                    </div>
                  )}
                  <form onSubmit={handleBundleScan} className="flex gap-2">
                    <Input ref={bundleInputRef} value={bundleValue} onChange={e => setBundleValue(e.target.value)} placeholder="Scan bundle barcode..." data-testid="bundle-scan-input" disabled={scanning} />
                    <Button type="submit" disabled={!bundleValue.trim() || scanning}><ScanBarcode className="w-4 h-4" /></Button>
                  </form>
                </div>
              </CardContent>
            </Card>

            {scannedBundles.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Bundles ({scannedBundles.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {scannedBundles.map((b, idx) => (
                      <div key={b.bundle_code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm" data-testid={`bundle-row-${b.bundle_code}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium truncate">{b.bundle_code}</span>
                            {b.is_legacy && <Badge variant="outline" className="text-[10px] h-4 border-amber-400 text-amber-700 bg-amber-50">legacy</Badge>}
                            {typeof b.usn_count === 'number' && <span className="text-xs text-gray-500">· {b.usn_count} USN</span>}
                          </div>
                          {(b.design_name || b.shade || b.size) && (
                            <div className="text-xs text-gray-500 truncate">
                              {b.design_name || ''}{b.shade ? ` · ${b.shade}` : ''}{b.size ? ` · ${b.size}` : ''}
                            </div>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeBundleAt(idx)}><XCircle className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Button className={`w-full ${shipType === 'site' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
              size="lg" onClick={handleCreateBox} disabled={creating || scannedBundles.length === 0} data-testid="create-box-btn">
              {creating ? 'Creating...' : `Create Box — ${scannedBundles.length} bundle${scannedBundles.length !== 1 ? 's' : ''}`}
            </Button>
          </>
        ) : (
          <Card className="border-green-200 bg-green-50/30" data-testid="ship-result-card">
            <CardContent className="pt-6">
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h2 className="text-lg font-bold text-green-800">Box Created</h2>
                <p className="text-sm font-mono text-gray-600">{result.box_number}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Destination</p>
                  <p className="font-bold">{result.destination_name}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Bundles / USNs</p>
                  <p className="font-bold">{result.total_bundles} / {result.total_usns}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Button className="w-full" onClick={handlePrintSlip} data-testid="print-slip-btn">
                  <Printer className="w-4 h-4 mr-2" /> Print A4 Slip
                </Button>
                <Button variant="outline" className="w-full" onClick={handleReset}>Create Another Box</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AssortmentShip;
